import { IResultCreateTypeObject } from '@/courses/interfaces/result-create-type-object.interface';
import { CreateSplitObjectMixin } from '@/courses/mixins/create-split-object.mixin';
import Component from 'vue-class-component';
import { Mixins, Prop, Watch } from 'vue-property-decorator';
import EdFormModel from '@/models/ed-form.model';
import EdInputModel from '@/models/ed-input.model';
import { required, maxLength, between, numeric } from 'vuelidate/lib/validators';
import { FILE_TO_WRITE_OBJECT } from '@/courses/values/courses.values';
import SectionSchoolCalendarMixin from '@/sections/mixins/section-school-calendar.mixin';
import { SectionProvider } from '@/sections/providers/section.provider';
import { SectionSchooolCalendarProvider } from '@/sections/providers/section-school-calendar.provider';
import KEYS from '@/values/keys';

@Component({
  name: 'ed-create-type-object',
  components: {
    EdSelectTag: () => import(/* EdSelectTag */'@/courses/components/ed-select-tag/ed-select-tag.component.vue')
  }
})
export default class EdCreateTypeObject extends Mixins(SectionSchoolCalendarMixin, CreateSplitObjectMixin) {

  @Prop() readonly product: any;
  @Prop() readonly hasGrading: boolean;

  public result: IResultCreateTypeObject = {
    question: null,
    solution: null,
    edition: {
      deleteQuestion: false,
      deleteSolution: false
    },
    name: '',
    answers: 0,
    answerPoints: [],
    dueDate: '',
    lastDate: '',
    labelSections: '',
  };
  public form: any = EdFormModel([
    EdInputModel('name', '', { required }),
  ]);
  public saving: boolean = false;
  public maxlength: number = 2;
  public answerPoints: string[] = [];
  public tags: Array<string> = ['TEST', 'CLASSWORK', 'HOMEWORK'];
  public tagSelected: string = null;
  public showListComponent: boolean = true;
  private MAX_ANSWERS_QUANTITY: number = 1;

  get title() {
    return this.params.title;
  }

  get canSave(){
    return this.problemFile.valid && this.form.valid && this.isSolutionValid;
  }

  get hasBeenResolvedByAnyStudent() {
    return this.params.hasBeenResolvedByAnyStudent ? this.params.hasBeenResolvedByAnyStudent : false;
  }

  get shouldArchiveCurrentHomeworks(){
    if(this.params.answers != this.form.answers.value){
      return true;
    }
    if(this.params.problem.type != this.problemFile.type){
      return true;
    }
    if(this.params.problem.type == FILE_TO_WRITE_OBJECT.COURSE_MATERIAL &&
      ( this.params.problem.data.sectionMaterial.materialId != this.problemFile.data.material.relatedContentId ||
        this.params.problem.data.sectionMaterial.startSection != this.problemFile.data.start ||
        this.params.problem.data.sectionMaterial.endSection != this.problemFile.data.end)){
      return true;
    }
    if(this.params.problem.type == FILE_TO_WRITE_OBJECT.FILE && this.problemFile.data instanceof File){
      return true;
    }
    return false;
  }

  get courseType(): string {
    return this.$store.state.CourseStore.courses.content.find(
      (course: any) => course.id === this.params.courseId
    )?.type;
  }

  get showDisclaimer(): boolean {
    return this.params.edition && ![KEYS.CONTENT_TYPE.COURSE_TEMPLATE, KEYS.CONTENT_TYPE.CST].includes(this.courseType);
  }

  get components() {
    return this.product.eduacComponents;
  }

  init(name: string = '', dueDate: string = '', lastDate: string = '', answerPoints: number[] = [], homeworkWeight: number = 1, homeworkPoints: number = 100) {
    this.answerPoints = [];
    this.form = EdFormModel([
      EdInputModel('name', name, { required, maxLength: maxLength(60) }),
      EdInputModel('answers', this.MAX_ANSWERS_QUANTITY, {}, true),
      EdInputModel('dueDate', dueDate, {}, true),
      EdInputModel('lastDate', lastDate, {}, true),
      EdInputModel('homeworkWeight', homeworkWeight, {numeric, between: between(1, 1000)}, true),
      EdInputModel('homeworkPoints', homeworkPoints, {numeric, between: between(1, 1000)}, true)
    ]);
    if (this.hasGrading) {
      this.form.setValidator('homeworkWeight', 'required', required);
      this.form.setValidator('homeworkPoints', 'required', required);
      for(let i = 0; i < answerPoints.length; i++){
        this.answerPoints.push(`answersPoints${i}`);
        this.form.setInput(EdInputModel(`answersPoints${i}`, answerPoints[i], { required, numeric, between: between(1, 1000) }, true));
      }
    }

  }

  onProblemChanged(result: { data: any, valid: boolean, type: string }) {
    this.problemFile = result;
    /* Set name of object for default */
    if(this.problemFile.data && !this.form.name.value)  {
      let name = this.problemFile.data.name.split('.')[0];
      this.form.set('name', name);
      this.form.setValid('name', true);
    }
    /* Check if the problem created previosly document was removed */
    if(this.params.edition) {
      this.result.edition.deleteQuestion = this.getIsTheSameCourseMaterial(this.params.problem, this.problemFile);
    }

    this.checkComponentSelected(this.problemFile);
  }

  onSolutionChanged(result: { data: any, valid: boolean, type: string }) {
    this.solutionFile = result;
    /* Check if the solution created previosly document was removed */
    if(this.params.edition) {
      this.result.edition.deleteSolution = this.getIsTheSameCourseMaterial(this.params.solution, this.solutionFile);
    }
  }

  save() {
    if(this.params.edition && this.params.resolved && this.shouldArchiveCurrentHomeworks) {
      this.$EdModal.createConfirmationModal(
        {
          body: this.$i18n.t('courses.typeObject.confirmEdit'), 
          action: () => {
            this.saveTypeObject();
          }
        }
      );
    } else {
      this.saveTypeObject();
    }
  }

  close() {
    this.$emit('close');
  }

  saveTypeObject() {
    this.$emit('save', this.getResultTypeObject());
  }

  setSaving(value: boolean) {
    this.saving = value;
  }

  getResultTypeObject(): IResultCreateTypeObject {
    const dueDateInfo = SectionProvider.getDueDateInfo(
      this.form,
      this.defaultDueDate,
      this.defaultLastDate,
      this.params.edition,
      this.params.section,
      this.params.submissionDates
    );
    this.result.question = this.problemFile;
    this.result.answers = this.form.answers.value;
    this.result.name = this.form.name.value;
    this.result.solution = this.solutionFile;
    this.result.dueDate = this.form.dueDate.value;
    this.result.lastDate = this.form.lastDate.value;
    this.result.dueDateAuto = dueDateInfo.dueDateAuto;
    this.result.lastDateAuto = dueDateInfo.lastDateAuto;
    this.result.labelSections = this.tagSelected;
    this.result.component= this.componentSelected;
    if(this.hasGrading){
      this.result.homeworkWeight = this.form.homeworkWeight.value;
      this.result.answerPoints.push(Number(this.form.homeworkPoints.value))
    } else {
      this.result.answerPoints = null;
    }
    return this.result;
  }

  setDataForEdition() {
    this.setProblemForEdition(this.params.problem, this.params.courseId);
    this.setSolutionForEdition(this.params.solution, this.params.courseId);
    const dates = this.params.submissionDates || {};
    this.init(
      this.params.name,
      SectionSchooolCalendarProvider.getSchoolDayDate(dates.dueSchoolDay),
      SectionSchooolCalendarProvider.getSchoolDayDate(dates.lastSchoolDay),
      this.params.answerPoints,
      this.params.homeworkWeight,
      this.params.answerPoints[0]
    );

    this.tagSelected = this.params.labelSections;

    this.hasBeenHomeworkResolvedByAnyStudent = this.hasBeenResolvedByAnyStudent;

    this.checkComponentSelected(this.params.problem);
  }

  checkComponentSelected(problemData) {
    if(this.params.hasTagSelector ) {
      if( problemData.type === 'COURSE_MATERIAL' && problemData.data ) {
        this.componentSelected = this.components.find(c => c.id == problemData.data.component?.id) ?? this.components.find(c => c.mandatory);
        this.showListComponent = false; 
      } else {
          this.componentSelected = this.componentSelected ? this.componentSelected : this.components.find(c => c.id == this.params.component?.id) ?? this.components.find(c => c.mandatory);
          this.showListComponent = true;
      }

      this.$edEventBus.$emit('updateSelected');
    }
  }

  updateAnswers(){
    if((this.params.answerPoints && !this.form.answers.value) || !this.hasGrading) //Delete answers on saved object
      return;

    let curAnswers = this.form.answers.value ? Number(this.form.answers.value) : 0
    if(curAnswers > this.answerPoints.length){
      for(let i = this.answerPoints.length; i < curAnswers; i++){
        let deftValue = i == 0 ? 101 - curAnswers : 1;
        this.answerPoints.push(`answersPoints${i}`);
        this.form.setInput(EdInputModel(`answersPoints${i}`, deftValue, { required, numeric, between: between(1, 1000) },true));
      }
    }else{
      for(let i = this.answerPoints.length - 1; i >= curAnswers; i--){
        this.answerPoints.pop();
        this.form.remove(`answersPoints${i}`);
      }
    }
  }

  validatePoints(){
    for(let i = 0; i < this.answerPoints.length; i++){
      if(!this.form[`answersPoints${i}`].valid){
        this.form.setValid('answers', false);
        return false;
      }
    }
    this.form.setValid('answers', true);
    return true;
  }

  beforeCreate() {
    this.componentSelected = null;
  }

  created() {
    if(this.params.edition) {
      this.setDataForEdition();
    }
  }

  mounted() {
    if(!this.params.edition) {
      let dueDate = this.hasSchoolDays ? this.defaultDueDate : '';
      let lastDate = this.hasSchoolDays ? this.defaultLastDate : '';
      this.init('', dueDate, lastDate);
      if(this.product.eduacComponents) {
        this.componentSelected = this.components.find(c => c.mandatory);
      }
    }
  }

}
